import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RelatedPartyPaymentService } from '../../services/related-party-payment.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-related-party-payment-type-ce',
    templateUrl: './related-party-payment-type.component.html',
    styleUrls: ['./related-party-payment-type.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RelatedPartyPaymentTypeComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  modalTitle = "Select Payment Type";
  minimise = false;
  crudLoading = false;

  // Component specific properties
  paymentTypes: any[] = [];
  selectedPaymentType: any = null;
  rowData: any = {};
  globalParams: any = {};

  constructor(
    private service: RelatedPartyPaymentService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    console.log('RelatedPartyPaymentTypeComponent initialized');
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.globalParams = header.globalParams || {};
    const gc = header.generalConfigData || {};
    const baseUrl = gc.base_url;
    if (baseUrl) {
      this.service.setBaseUrl(baseUrl);
    }
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? (rawRow[0] && typeof rawRow[0] === 'object' ? rawRow[0] : {}) : (rawRow || {});
    this.cdr.markForCheck();
  }

  init(): void {
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.loadPaymentTypes();
    }
  }

  /**
   * Load payment types from API
   */
  loadPaymentTypes(): void {
    console.log('Loading payment types for rowData:', this.rowData);

    this.service.getPaymentTypes(this.rowData, this.globalParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any[]) => {
        this.paymentTypes = data;
        console.log('Payment types loaded:', this.paymentTypes);
        this.cdr.markForCheck();
      },
      (error) => {
        console.error('Error loading payment types:', error);
        this.showError(error.message || 'Error loading payment types');
      }
    );
  }

  /**
   * Save selected payment type
   */
  savePartyPaymentType(): void {
    if (!this.selectedPaymentType) {
      this.showError('Please select a payment type');
      return;
    }

    this.crudLoading = true;
    this.cdr.markForCheck();

    this.service.savePaymentType(this.rowData, this.selectedPaymentType, this.globalParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;

        if (response.callSuccess === '1') {
          this.showSuccess('Payment Type saved successfully');

          this.requestGridUpdate.emit('refresh');
        } else {
          this.showError(response.errorMessage || 'Failed to save payment type');
        }
        this.cdr.markForCheck();
      },
      (error) => {
        this.crudLoading = false;
        console.error('Error saving payment type:', error);
        this.showError('Error saving payment type');
        this.cdr.markForCheck();
      }
    );
  }

  /**
   * Cancel and close modal
   */
  cancel(): void {
    this.crudLoading = false;
    this.requestGridUpdate.emit('cancel');
    this.cdr.markForCheck();
  }

  /**
   * Show success message
   */
  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: ['error-snackbar']
    });
  }
}
